/**
 * Запуск задания (E4.6c): задание открывается в учебном экземпляре на странице, где
 * пользователю предстоит следующее действие. Пройденное или запрошенное заново задание
 * начинается с пустыми целями -- рабочие данные и другие уроки сброс не затрагивает. Повтор сбрасывает только цели этого задания; сохранённый разбор или политику можно изменить и сохранить снова.
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { TrainingManifest } from '@/api/training';
import { useWorkspaceDataSource } from '@/features/workspace/WorkspaceDataSourceContext';
import { hasDirtyDrafts } from '@/stores/workspaceDrafts';
import { exerciseRoute, type ExerciseId, stepRoute } from './catalog';
import { openExercise, useExerciseStore } from './exerciseStore';
import { startExercise, type TrainingProgress } from './progress';
import { leaveTour } from './tourStore';
import { useTrainingLauncher } from './useTrainingLauncher';
import { useTrainingProgress } from './useTrainingProgress';

/** Страница оставшегося действия (макет trainingExerciseResumeHref) */
export function exerciseResumeRoute(
	progress: TrainingProgress,
	id: ExerciseId,
	manifest: TrainingManifest,
): string {
	const done = progress.exercises[id]?.done.length ?? 0;
	if (id === 'ai' && done > 0) return '/llm-gate/events';
	if (id === 'daily' && done === 1) return '/activity-log';
	if (id === 'daily' && done === 2) return stepRoute('daily-assignment', manifest);
	return exerciseRoute(id);
}

export function useTrainingExercise() {
	const { t } = useTranslation('learning');
	const { enterTraining, mode } = useWorkspaceDataSource();
	const { progress, update } = useTrainingProgress();
	const { manifestOf, newExample } = useTrainingLauncher();

	const start = useCallback(
		async (id: ExerciseId, options: { restart?: boolean } = {}) => {
			if (hasDirtyDrafts()) {
				toast.error(t('tour.dirtyStart'));
				return;
			}
			try {
				const restart = options.restart || progress.exercises[id]?.status === 'completed';
				const manifest = await manifestOf();
				if (manifest === null) {
					/* Повтор не поможет: пример удалён или истёк -- предлагается новый */
					toast.error(t('page.instanceGone'), {
						action: { label: t('page.newExample'), onClick: () => void newExample() },
					});
					return;
				}
				const next = startExercise(progress, id, restart);
				update((current) => startExercise(current, id, restart));
				leaveTour('close', false);
				openExercise(id);
				enterTraining(manifest.instance_id, exerciseResumeRoute(next, id, manifest));
			} catch {
				toast.error(t('page.openFailed'));
			}
		},
		[enterTraining, manifestOf, newExample, progress, t, update],
	);

	const resume = useCallback(
		(id: ExerciseId) => {
			if (mode !== 'live' && useExerciseStore.getState().paused === id) {
				leaveTour('close', false);
				openExercise(id);
				return;
			}
			void start(id);
		},
		[mode, start],
	);

	return { start, resume };
}
